/**
 * Game Board component - displays the Connect Four grid and handles piece dropping
 */

import React, { useState, useEffect } from 'react';
import { IGameBoardProps } from '../types';
import { GameBoardPreview } from './game-board-preview';
import { playRandomWinSound, playRandomGameOverSound } from '../utils/sound-utils';
import { GameMode, GameStatus, PlayerType } from '../types';
import './game-board.scss';

export const GameBoard: React.FC<IGameBoardProps> = ({
  board,
  onColumnClick,
  winningLine,
  isGameActive,
  currentPlayer,
  gameConfig,
  gameStatus,
  winner,
  lastAIMove,
}) => {
  const [hasPlayedWinSound, setHasPlayedWinSound] = useState<boolean>(false);
  const [droppingCell, setDroppingCell] = useState<{ row: number; col: number } | null>(null);

  // Play win/lose sounds when game ends
  useEffect(() => {
    if (gameStatus === GameStatus.Won && winner && !hasPlayedWinSound) {
      const isPlayerVsAI = gameConfig?.mode === GameMode.PlayerVsComputer;
      const humanPlayer = gameConfig?.player1;
      const isHumanWinner = winner === humanPlayer?.id;

      if (isPlayerVsAI) {
        // Playing against AI
        if (isHumanWinner) {
          // Human player won against AI
          playRandomWinSound();
        } else {
          // Human player lost to AI
          playRandomGameOverSound();
        }
      } else {
        // Player vs Player mode - always play win sound
        playRandomWinSound();
      }

      setHasPlayedWinSound(true);
    }
  }, [gameStatus, winner, gameConfig, hasPlayedWinSound]);

  // Reset the sound flag when a new game starts
  useEffect(() => {
    if (gameStatus === GameStatus.InProgress) {
      setHasPlayedWinSound(false);
    }
  }, [gameStatus]);

  // Trigger drop animation when AI makes a move
  useEffect(() => {
    if (lastAIMove != null) {
      setDroppingCell({ row: lastAIMove.row, col: lastAIMove.column });
      setTimeout(() => setDroppingCell(null), 550);
    }
  }, [lastAIMove]);

  const findTargetRow = (column: number): number => {
    for (let row = board.length - 1; row >= 0; row--) {
      if (board[row][column] === null) return row;
    }
    return -1;
  };

  const handleColumnClick = (column: number) => {
    if (!isGameActive || droppingCell !== null) return;
    const targetRow = findTargetRow(column);
    if (targetRow === -1) return;
    setDroppingCell({ row: targetRow, col: column });
    onColumnClick(column);
    setTimeout(() => setDroppingCell(null), 550);
  };

  const isColumnAvailable = (column: number): boolean => {
    return board[0][column] === null;
  };

  const getCellClassName = (row: number, col: number): string => {
    const baseClass = 'game-board__cell';
    const piece = board[row][col];

    let classes = [baseClass];

    if (piece) {
      classes.push(`${baseClass}--${piece}`);
    } else {
      classes.push(`${baseClass}--empty`);
    }

    // Highlight winning pieces
    if (winningLine?.positions.some(pos => pos.row === row && pos.column === col)) {
      classes.push(`${baseClass}--winning`);
    }

    if (droppingCell?.row === row && droppingCell?.col === col) {
      classes.push(`${baseClass}--dropping`);
    }

    return classes.join(' ');
  };

  const getColumnClassName = (column: number): string => {
    const baseClass = 'game-board__column';
    let classes = [baseClass];

    if (isGameActive) {
      classes.push(`${baseClass}--interactive`);
    }

    if (isGameActive && board[0][column] === null) {
      classes.push(`${baseClass}--available`);
      classes.push(`${baseClass}--player-${currentPlayer}`);
    }

    return classes.join(' ');
  };

  return (
    <div
      className='game-board-container'
      data-testid='game-board-container'
      data-tdx-mouseless='true'
    >
      <div className='game-board' data-testid='game-board'>
        <GameBoardPreview
          currentPlayer={currentPlayer}
          isGameActive={isGameActive}
          onColumnClick={handleColumnClick}
          isColumnAvailable={isColumnAvailable}
        />

        <div className='game-board__grid'>
          {Array.from({ length: 7 }, (_, colIndex) => (
            <div
              key={`column-${colIndex}`}
              className={getColumnClassName(colIndex)}
              onClick={() => handleColumnClick(colIndex)}
            >
              {Array.from({ length: 6 }, (_, rowIndex) => {
                const isDropping = droppingCell?.row === rowIndex && droppingCell?.col === colIndex;
                // Distance from preview zone to this cell: preview height + grid padding + rows above
                const dropStyle = isDropping
                  ? ({
                      '--drop-distance': `calc(-${rowIndex * 6}vw - ${rowIndex * 0.5 + 5.5}rem)`,
                    } as React.CSSProperties)
                  : undefined;
                return (
                  <div
                    key={`cell-${rowIndex}-${colIndex}`}
                    className={getCellClassName(rowIndex, colIndex)}
                    data-testid={`cell-${rowIndex}-${colIndex}`}
                    style={dropStyle}
                  >
                    <div className='game-board__piece-container'>
                      {board[rowIndex][colIndex] && <div className='game-board__piece' />}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {winningLine && (
        <div className='game-board__winning-overlay'>
          <div className='game-board__winning-message'>
            <span className='game-board__winner-text'>Player {currentPlayer} Wins!</span>
          </div>
        </div>
      )}
    </div>
  );
};
